<script lang="ts">
  import clsx from 'clsx';
  import { Card } from '@athena-lib/Card';
  import { Image } from '@athena-lib/Image';
  import { Stack, StackItem } from '@athena-lib/Stack';
  import { BaseHeading, Paragraph } from '@athena-lib/Text';
  import { setContainerContextWidth } from '@athena-lib/container-context.svelte';
  import { parseHeadingLevel } from '@athena-lib/Text/BaseHeading/types';
  import type { IconCardProps } from '../types';

  const {
    title,
    description,
    details,
    titleHeadingTag,
    image,
    class: className,
    hideDescriptionInMobile = true,
    useMaxHeight = false,
    horizontalOnMobile = false,
    horizontal = false,
    ...props
  }: IconCardProps = $props();

  const iconCardClasses = $derived.by(() => {
    if (horizontalOnMobile) {
      return clsx('flex-col transition-transform hover:scale-105', className);
    } else {
      return clsx(
        'min-h-full w-auto transition-transform hover:scale-105',
        useMaxHeight && 'max-h-96',
        className,
      );
    }
  });

  setContainerContextWidth({
    sm: 0.5,
    md: 0.5,
    lg: 0.5,
  });

  const aspectRatio = horizontalOnMobile || horizontal ? 'auto' : '4/5';
</script>

<Card {...props} padding="medium" class={iconCardClasses} aspect={aspectRatio}>
  {#if horizontalOnMobile || horizontal}
    <div class="flex aspect-[3/1] flex-row items-center">
      <div class="mr-4 flex aspect-square max-w-28 items-center object-contain">
        <Image src={image.url} alt={image.alt} loading="lazy" />
      </div>
      <Stack space="none" class="justify-center">
        {#if title}
          <StackItem>
            <BaseHeading
              level={parseHeadingLevel(titleHeadingTag) ?? 3}
              size={{ sm: 'small', md: 'xsmall', lg: 'xsmall' }}
            >
              {title}
            </BaseHeading>
          </StackItem>
        {/if}
        {#if description}
          <StackItem>
            <Paragraph>{description}</Paragraph>
          </StackItem>
        {/if}
        {#if details}
          <StackItem>
            <Paragraph>{details}</Paragraph>
          </StackItem>
        {/if}
      </Stack>
    </div>
  {:else}
    <Stack space="small" class="flex h-full">
      <StackItem class="flex aspect-square items-center justify-center lg:-mt-7">
        <div class="mx-auto flex w-[80%] justify-center object-contain">
          <Image src={image.url} alt={image.alt} loading="lazy" />
        </div>
      </StackItem>
      <StackItem>
        <Stack space="none">
          {#if title}
            <StackItem class="mb-0 text-center leading-snug">
              <BaseHeading level={3} size={{ sm: 'small', md: 'xsmall', lg: 'xsmall' }}>
                {title}
              </BaseHeading>
            </StackItem>
          {/if}
          {#if description}
            <StackItem class={clsx(hideDescriptionInMobile && 'hidden md:block', 'text-center')}>
              <Paragraph>{description}</Paragraph>
            </StackItem>
          {/if}
          {#if details}
            <StackItem class={clsx(hideDescriptionInMobile && 'hidden md:block', 'text-center')}>
              <Paragraph>{details}</Paragraph>
            </StackItem>
          {/if}
        </Stack>
      </StackItem>
    </Stack>
  {/if}
</Card>
